import { Injectable } from '@angular/core';
import { ActiveCartService } from '@cart/data-access';
import { TimeSlots } from '@checkout/const';
import { CheckoutActions, CheckoutApiService, CheckoutSelectors, StateWithCustomCheckout } from '@checkout/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { ZoneFinderService } from '../services';

@Injectable()
export class TimeSlotsEffects {
  loadTimeSlots$: Observable<CheckoutActions.LoadTimeSlotsSuccess | CheckoutActions.LoadTimeSlotsFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.LOAD_TIME_SLOTS),
        // HYB2-7871 таймслоты должны загружаться только после
        // отправки данных на роут кастомерконсаймента
        switchMap((action) =>
          this.store.select(CheckoutSelectors.getCustomerConsignmentState).pipe(
            filter((state) => Boolean(!state.loading && state.success)),
            take(1),
            map(() => action),
          ),
        ),
        map((action: CheckoutActions.LoadTimeSlots) => action.payload),
        mergeMap((payload) =>
          combineLatest({
            cartId: this.activeCartService.getActiveCartId(),
          }).pipe(
            take(1),
            map(({ cartId }) => ({ cartId, polygonId: payload.polygonId })),
          ),
        ),
        filter(({ cartId }) => !!cartId),
        switchMap(({ cartId, polygonId }: { cartId: string; polygonId: string }) =>
          this.checkoutApiService.loadTimeSlots(cartId, polygonId).pipe(
            map((data: TimeSlots) => new CheckoutActions.LoadTimeSlotsSuccess(data)),
            catchError((error) => of(new CheckoutActions.LoadTimeSlotsFail(error))),
          ),
        ),
      ),
  );

  resetTimeSlotMode$: Observable<CheckoutActions.ResetTimeSlotsMode> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CheckoutActions.LOAD_TIME_SLOTS_SUCCESS,
        CheckoutActions.SELECT_ACTIVE_DELIVERY_ADDRESS,
        CheckoutActions.SET_ACTIVE_RECEIPT_TYPE,
      ),
      map(() => new CheckoutActions.ResetTimeSlotsMode()),
    ),
  );

  resetTimeSlotsAfterChangeActiveReceiptType$: Observable<CheckoutActions.TimeSlotsReset> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.SET_ACTIVE_RECEIPT_TYPE),
      map(() => {
        return new CheckoutActions.TimeSlotsReset();
      }),
    ),
  );

  loadTimeSlotsAfterSelectActiveDeliveryAddress$: Observable<
    CheckoutActions.LoadTimeSlots | CheckoutActions.LoadTimeSlotsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.SELECT_ACTIVE_DELIVERY_ADDRESS),
      map((action: CheckoutActions.SelectActiveDeliveryAddress) => action.payload),
      filter((addressId) => addressId !== undefined),
      switchMap((addressId: string) => this.zoneFinderService.getZone(addressId).pipe(take(1))),
      map((zone) => {
        if (!zone) {
          return new CheckoutActions.LoadTimeSlotsFail([]);
        }

        return new CheckoutActions.LoadTimeSlots({ polygonId: zone.polygonKey });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private checkoutApiService: CheckoutApiService,
    private activeCartService: ActiveCartService,
    private zoneFinderService: ZoneFinderService,
    private store: Store<StateWithCustomCheckout>,
  ) {}
}
